import { datadogLogs } from '@datadog/browser-logs';

import config from 'config';
import { AuthStore } from 'stores/auth';
import { LocalStorageKey } from 'utils/localstorage';
import { VERSION } from 'version';
const { datadogClientToken = '', datadogSite = '' } = config;

datadogLogs.init({
  clientToken: datadogClientToken,
  site: datadogSite,
  forwardErrorsToLogs: true,
  sessionSampleRate: 100,
  silentMultipleInit: true,
  proxy: undefined, // Ensure no proxy issues
  // Add rate limiting and timeout configuration
  service: 'florence-web',
  beforeSend: (log: any) => {
    // Add request timeout protection
    if (log.error?.name === 'TimeoutError') {
      return false; // Don't send timeout errors to prevent loops
    }
    return log;
  },
});

export function getEnvironmentFromHost() {
  const host = window.location.host;

  if (config.env === 'development') {
    return 'LOCAL';
  }

  const patterns = [
    { pattern: /\.tst\./, environment: 'TST' },
    { pattern: /\.scr\./, environment: 'SCR' },
    { pattern: /\.tmp\./, environment: 'TMP' },
    { pattern: /\.dev\./, environment: 'DEV' },
    { pattern: /\.qa\./, environment: 'QA' },
  ];

  for (const { pattern, environment } of patterns) {
    if (pattern.test(host)) {
      return environment;
    }
  }

  return 'PRD';
}

// Local buffer for failed logs
interface BufferedLog {
  level: string;
  message: string;
  options: object;
  timestamp: number;
  retryCount: number;
}

class RateLimitedLogBuffer {
  // Enhanced config values for rate limiting and batching
  private readonly buffer: BufferedLog[] = [];
  private readonly maxBufferSize = 1000;
  private readonly maxRetries = 3; // Reduced from 5
  private readonly retryInterval = 5000; // Reduced from 30s to 5s

  // Rate limiting configuration
  private readonly maxLogsPerSecond = 5; // Max 5 logs per second
  private readonly batchSize = 10; // Send logs in batches of 10
  private readonly batchInterval = 2000; // Batch every 2 seconds

  // Rate limiting state
  private logTimestamps: number[] = [];
  private isRetrying = false;
  private isBatching = false;
  private batchTimer: NodeJS.Timeout | null = null;

  addLog(level: string, message: string, options: object) {
    const log: BufferedLog = {
      level,
      message,
      options,
      timestamp: Date.now(),
      retryCount: 0,
    };

    // Add to buffer (don't try immediate send anymore)
    this.buffer.push(log);

    // Prevent buffer overflow
    if (this.buffer.length > this.maxBufferSize) {
      this.buffer.shift(); // Remove oldest log
      console.warn('DataDog log buffer overflow, discarding oldest log');
    }

    // Start batching if not already started
    this.startBatching();
  }

  private startBatching() {
    if (this.isBatching || this.isRetrying) return;

    this.isBatching = true;
    this.batchTimer = setTimeout(() => {
      this.processBatch();
    }, this.batchInterval);
  }

  private isRateLimited(): boolean {
    const now = Date.now();
    // Remove timestamps older than 1 second
    this.logTimestamps = this.logTimestamps.filter((t) => now - t < 1000);

    // Check if we're hitting rate limit
    return this.logTimestamps.length >= this.maxLogsPerSecond;
  }

  private processBatch() {
    if (this.buffer.length === 0) {
      this.isBatching = false;
      return;
    }

    // Check rate limit
    if (this.isRateLimited()) {
      // Delay and try again
      this.batchTimer = setTimeout(() => {
        this.processBatch();
      }, 1000); // Wait 1 second if rate limited
      return;
    }

    // Get a batch of logs to process
    const batchSize = Math.min(this.batchSize, this.buffer.length);
    const logsToProcess = this.buffer.splice(0, batchSize);
    const successfulLogIndices: number[] = [];

    logsToProcess.forEach((log, index) => {
      if (log.retryCount >= this.maxRetries) {
        // Log exceeded max retries, mark as successful to remove it
        successfulLogIndices.push(index);
        console.error(
          'DataDog log exceeded max retries, discarding:',
          log.message,
        );
        return;
      }

      try {
        // Record this send attempt for rate limiting
        this.logTimestamps.push(Date.now());

        datadogLogs.logger[log.level](log.message, {
          ...log.options,
          originalTimestamp: log.timestamp,
          retryCount: log.retryCount,
          batchedAt: Date.now(),
        });

        // Mark as successful
        successfulLogIndices.push(index);
      } catch (error) {
        // Increment retry count and put back in buffer
        log.retryCount++;
        this.buffer.unshift(log); // Put back at front for retry
        console.warn('DataDog log send failed, will retry:', error);
      }
    });

    // Continue batching if there are more logs or failed logs were re-added
    if (this.buffer.length > 0) {
      this.batchTimer = setTimeout(() => {
        this.processBatch();
      }, this.batchInterval);
    } else {
      this.isBatching = false;
    }
  }

  private scheduleRetry() {
    if (this.isRetrying) return;

    this.isRetrying = true;
    setTimeout(() => {
      this.processBuffer();
    }, this.retryInterval);
  }

  private processBuffer() {
    // Use the new batching system for retries too
    this.isRetrying = false;
    if (this.buffer.length > 0 && !this.isBatching) {
      this.startBatching();
    }
  }

  // Get buffer status for debugging
  getBufferStatus() {
    return {
      bufferedLogs: this.buffer.length,
      isRetrying: this.isRetrying,
      isBatching: this.isBatching,
      currentRateLimit: this.logTimestamps.length,
      maxRateLimit: this.maxLogsPerSecond,
      oldestLog:
        this.buffer.length > 0 ? new Date(this.buffer[0].timestamp) : null,
    };
  }

  // Force flush buffer (useful for debugging)
  forceFlush() {
    if (this.batchTimer) {
      clearTimeout(this.batchTimer);
      this.batchTimer = null;
    }
    this.isBatching = false;
    this.processBatch();
  }

  // Clear buffer (useful for testing)
  clearBuffer() {
    if (this.batchTimer) {
      clearTimeout(this.batchTimer);
      this.batchTimer = null;
    }
    this.buffer.length = 0;
    this.isRetrying = false;
    this.isBatching = false;
    this.logTimestamps = [];
  }
}

const logBuffer = new RateLimitedLogBuffer();

const logMethods = {};

['debug', 'info', 'error'].forEach((fn) => {
  logMethods[fn] = (
    ...logs: [string, object & { ConversationId?: string; TraceId?: string }]
  ) => {
    const [message, options = {}] = logs;
    const { currentAccount } = AuthStore.getRawState();

    // Additional throttling for production environments
    const env = getEnvironmentFromHost();
    if (env === 'PRD' && fn === 'debug') {
      // Skip debug logs in production to reduce volume
      return;
    }

    // Skip excessive TimeLogger messages in production
    if (env === 'PRD' && message.includes('Web Operation')) {
      // Only log major operations, not every small step
      const majorOperations = [
        'ApiService.sendMessage',
        'ApiService.streamMessage',
        'ApiService.getNewConversation',
      ];
      if (!majorOperations.some((op) => message.includes(op))) {
        return;
      }
    }

    const localAccountId =
      currentAccount?.localAccountId ||
      localStorage.getItem(LocalStorageKey.localAccountId);

    const defaultOptions: { [key: string]: any } = {
      service: 'florence-web',
      env: getEnvironmentFromHost(),
      app_version: VERSION,
      SessionId: localStorage.getItem(LocalStorageKey.sessionId),
      UserId: localAccountId,
    };

    if (options.ConversationId) {
      defaultOptions.ConversationId = options.ConversationId;
    }
    if (options.TraceId) {
      defaultOptions.TraceId = options.TraceId;
    }

    // Use buffer instead of direct logging
    logBuffer.addLog(fn, message, {
      ...defaultOptions,
      ...options,
    });
  };
});

export const DataDogLog = {
  log: logMethods['info'],
  info: logMethods['info'],
  error: logMethods['error'],
  debug: logMethods['debug'],
  // Add buffer monitoring utilities
  getBufferStatus: () => logBuffer.getBufferStatus(),
  forceFlushBuffer: () => logBuffer.forceFlush(),
  clearBuffer: () => logBuffer.clearBuffer(),
};

export const sendAnalytics = {
  userLogout: () => {
    DataDogLog.info('user logout');
  },
  userLogin: () => {
    DataDogLog.info('user login');
  },
};
